import React, { useEffect, useState } from "react";
import { Phone, Menu, X } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const LOGO_URL = "/logo.png";

export const Navbar = () => {
  const { lang, setLang, t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "#strengths", label: t.nav.strengths },
    { href: "#services", label: t.nav.services },
    { href: "#reviews", label: t.nav.reviews },
    { href: "#contact", label: t.nav.contact },
  ];

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-white/90 border-b border-slate-200"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-24 flex items-center justify-between">
        <a href="#top" className="flex items-center gap-3" data-testid="navbar-logo">
          <img
            src={LOGO_URL}
            alt="ZEMKA Gebäudereinigung Logo"
            className="h-16 sm:h-20 w-auto transition-all duration-300"
            style={{
              filter: scrolled ? 'none' : 'invert(1) brightness(1.05)',
            }}
          />
          <span className="sr-only">ZEMKA Gebäudereinigung</span>
        </a>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className={`text-sm font-semibold transition-colors ${
                scrolled ? "text-slate-700 hover:text-cyan-600" : "text-white/90 hover:text-cyan-300"
              }`}
              data-testid={`navlink-${l.href.replace("#", "")}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div
            className="hidden sm:flex items-center border border-slate-300 rounded-none overflow-hidden"
            data-testid="lang-toggle"
          >
            <button
              type="button"
              onClick={() => setLang("de")}
              className={`px-3 py-1.5 text-xs font-bold tracking-wide ${
                lang === "de" ? "bg-[#0F172A] text-white" : "bg-white text-slate-600 hover:bg-slate-100"
              }`}
              data-testid="lang-de"
            >
              DE
            </button>
            <button
              type="button"
              onClick={() => setLang("en")}
              className={`px-3 py-1.5 text-xs font-bold tracking-wide ${
                lang === "en" ? "bg-[#0F172A] text-white" : "bg-white text-slate-600 hover:bg-slate-100"
              }`}
              data-testid="lang-en"
            >
              EN
            </button>
          </div>

          <a
            href="#contact"
            className="hidden md:inline-flex items-center gap-2 bg-[#06B6D4] hover:bg-[#0891B2] text-white font-semibold px-5 py-3 text-sm transition-colors"
            data-testid="navbar-cta"
          >
            {t.nav.cta}
          </a>

          <a
            href="tel:01708251779"
            className="md:hidden inline-flex items-center justify-center bg-[#06B6D4] text-white p-2.5"
            data-testid="navbar-phone-mobile"
            aria-label="Call ZEMKA"
          >
            <Phone size={18} strokeWidth={2} />
          </a>

          <button
            type="button"
            className="md:hidden p-2 text-slate-700"
            onClick={() => setOpen((o) => !o)}
            data-testid="navbar-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden bg-white border-t border-slate-200" data-testid="navbar-mobile-menu">
          <div className="px-6 py-4 flex flex-col gap-3">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="py-2 text-base font-semibold text-slate-800"
                data-testid={`navlink-mobile-${l.href.replace("#", "")}`}
              >
                {l.label}
              </a>
            ))}
            <div className="flex items-center gap-2 pt-2">
              <button
                type="button"
                onClick={() => setLang("de")}
                className={`flex-1 py-2 text-xs font-bold border ${
                  lang === "de" ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white text-slate-600 border-slate-300"
                }`}
                data-testid="lang-de-mobile"
              >
                DEUTSCH
              </button>
              <button
                type="button"
                onClick={() => setLang("en")}
                className={`flex-1 py-2 text-xs font-bold border ${
                  lang === "en" ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white text-slate-600 border-slate-300"
                }`}
                data-testid="lang-en-mobile"
              >
                ENGLISH
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
